import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { 
  Apple, 
  Heart, 
  Activity, 
  Phone, 
  Calendar, 
  User,
  Scale,
  Target,
  CheckCircle,
  AlertCircle,
  Utensils,
  Clock,
  TrendingUp,
  BookOpen
} from 'lucide-react';
import { generateWhatsAppLink, mockUser } from '../mock/mockData';

const Nutrition = () => {
  const [selectedCondition, setSelectedCondition] = useState('');
  const [consultationForm, setConsultationForm] = useState({
    name: '',
    phone: '',
    age: '',
    condition: '',
    currentWeight: '',
    targetWeight: '',
    dietaryRestrictions: '',
    medicalHistory: ''
  });

  const ncdConditions = [
    {
      id: 'diabetes',
      name: 'Diabetes Management',
      icon: '🩸',
      description: 'Blood sugar control through balanced nutrition',
      keyPoints: [
        'Low glycemic index foods',
        'Portion control and meal timing',
        'Fiber-rich vegetables and whole grains',
        'Limited processed sugars'
      ],
      sampleMeal: 'Brown rice, grilled chicken, mixed vegetables, small portion of fruits'
    },
    {
      id: 'hypertension',
      name: 'Hypertension (High BP)',
      icon: '❤️',
      description: 'Heart-healthy diet to manage blood pressure',
      keyPoints: [
        'Low sodium (DASH diet)',
        'Rich in potassium and magnesium',
        'Limit processed and packaged foods',
        'Include fruits, vegetables, lean proteins'
      ],
      sampleMeal: 'Oats with berries, grilled fish, steamed vegetables, nuts'
    },
    {
      id: 'cholesterol',
      name: 'High Cholesterol',
      icon: '🧈',
      description: 'Cholesterol-lowering nutrition plan',
      keyPoints: [
        'Omega-3 rich foods (fish, walnuts)',
        'Soluble fiber (oats, beans, apples)',
        'Avoid trans fats and saturated fats',
        'Plant sterols and stanols'
      ],
      sampleMeal: 'Oatmeal with almonds, salmon, quinoa salad, olive oil dressing'
    },
    {
      id: 'obesity',
      name: 'Weight Management',
      icon: '⚖️',
      description: 'Sustainable weight loss through nutrition',
      keyPoints: [
        'Calorie deficit with balanced macros',
        'High protein to preserve muscle',
        'Complex carbohydrates',
        'Regular meal timing'
      ],
      sampleMeal: 'Greek yogurt, lean protein, vegetables, healthy fats in moderation'
    },
    {
      id: 'thyroid',
      name: 'Thyroid Disorders',
      icon: '🦋',
      description: 'Nutrition support for thyroid health',
      keyPoints: [
        'Iodine-rich foods (in moderation)',
        'Selenium sources (Brazil nuts, fish)',
        'Avoid goitrogenic foods if needed',
        'Support metabolism naturally'
      ],
      sampleMeal: 'Seafood, Brazil nuts, leafy greens, whole grains'
    },
    {
      id: 'kidney',
      name: 'Kidney Health',
      icon: '🫘',
      description: 'Kidney-friendly nutrition guidance',
      keyPoints: [
        'Controlled protein intake',
        'Limited phosphorus and potassium',
        'Adequate hydration',
        'Monitor electrolyte balance'
      ],
      sampleMeal: 'Low-protein grains, limited dairy, controlled portions'
    }
  ];

  const nutritionistPackages = [
    {
      id: 'basic',
      name: 'Basic Consultation',
      duration: '30 minutes',
      price: 799,
      subscriberPrice: 599,
      features: [
        'Initial diet assessment',
        'Basic meal plan (7 days)',
        'General nutrition guidelines',
        'WhatsApp follow-up support'
      ]
    },
    {
      id: 'comprehensive',
      name: 'Comprehensive Plan',
      duration: '45 minutes + follow-ups',
      price: 1499,
      subscriberPrice: 999,
      features: [
        'Detailed nutritional analysis',
        'Personalized meal plans (30 days)',
        'Recipe suggestions',
        'Weekly progress tracking',
        '4 follow-up consultations'
      ],
      popular: true
    },
    {
      id: 'premium',
      name: 'Premium NCD Management',
      duration: '60 minutes + 3 months support',
      price: 2999,
      subscriberPrice: 2199,
      features: [
        'Complete metabolic assessment',
        'NCD-specific diet protocols',
        'Supplement recommendations',
        'Monthly body composition analysis',
        '12 follow-up sessions',
        'Family diet education'
      ]
    }
  ];

  const handleConsultationBooking = (packageType) => {
    const consultationData = {
      ...consultationForm,
      package: packageType.name,
      price: packageType.subscriberPrice
    };

    const whatsAppMessage = `Nutrition Consultation Booking
Name: ${consultationForm.name || 'Not provided'}
Phone: ${consultationForm.phone || 'Not provided'}
Age: ${consultationForm.age || 'Not provided'}
Condition: ${consultationForm.condition || selectedCondition || 'General nutrition'}
Package: ${packageType.name} - ₹${packageType.subscriberPrice}
Current Weight: ${consultationForm.currentWeight || 'Not provided'}
Target Weight: ${consultationForm.targetWeight || 'Not provided'}
Dietary Restrictions: ${consultationForm.dietaryRestrictions || 'None'}
Medical History: ${consultationForm.medicalHistory || 'None'}

Please confirm appointment slot and provide further instructions.`;

    const whatsAppLink = `https://wa.me/917337541113?text=${encodeURIComponent(whatsAppMessage)}`;
    window.open(whatsAppLink, '_blank');
  };

  const selectedConditionData = ncdConditions.find(condition => condition.id === selectedCondition);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Nutrition & Diet Counselling
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Expert nutrition guidance for Non-Communicable Diseases (NCD) management. 
              Get personalized diet plans from certified nutritionists across India.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* NCD Conditions */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Apple className="w-6 h-6 text-green-600" />
                  <span>Select Your Health Condition</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {ncdConditions.map((condition) => (
                    <Card 
                      key={condition.id}
                      className={`cursor-pointer transition-all duration-300 ${
                        selectedCondition === condition.id 
                          ? 'border-orange-400 bg-orange-50' 
                          : 'border-gray-200 hover:border-orange-300 hover:bg-orange-25'
                      }`}
                      onClick={() => setSelectedCondition(condition.id)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start space-x-3">
                          <span className="text-2xl">{condition.icon}</span>
                          <div className="flex-1">
                            <h3 className="font-semibold text-gray-900 mb-1">
                              {condition.name}
                            </h3>
                            <p className="text-sm text-gray-600">
                              {condition.description}
                            </p>
                          </div>
                          {selectedCondition === condition.id && (
                            <CheckCircle className="w-5 h-5 text-orange-600" />
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Selected Condition Details */}
            {selectedConditionData && (
              <Card className="border-green-200 bg-green-50">
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2 text-green-800">
                    <span className="text-2xl">{selectedConditionData.icon}</span>
                    <span>{selectedConditionData.name} - Nutrition Guidelines</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-semibold text-green-900 mb-3">Key Nutrition Points:</h4>
                      <ul className="space-y-2">
                        {selectedConditionData.keyPoints.map((point, index) => (
                          <li key={index} className="flex items-start space-x-2">
                            <CheckCircle className="w-4 h-4 text-green-600 mt-1 flex-shrink-0" />
                            <span className="text-green-800 text-sm">{point}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-semibold text-green-900 mb-3">Sample Meal Idea:</h4>
                      <div className="bg-white p-4 rounded-lg border border-green-200">
                        <p className="text-green-800 text-sm">
                          {selectedConditionData.sampleMeal}
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Consultation Packages */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Calendar className="w-6 h-6 text-orange-600" />
                  <span>Nutrition Consultation Packages</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 gap-6">
                  {nutritionistPackages.map((pkg) => (
                    <Card 
                      key={pkg.id} 
                      className={`relative ${pkg.popular ? 'border-orange-400 shadow-lg' : 'border-gray-200'}`}
                    >
                      {pkg.popular && (
                        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                          <Badge className="bg-orange-600 text-white px-4 py-1">
                            Most Popular
                          </Badge>
                        </div>
                      )}
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-2">
                              {pkg.name}
                            </h3>
                            <p className="text-gray-600 text-sm mb-2">{pkg.duration}</p>
                            <div className="flex items-center space-x-2">
                              <span className="text-2xl font-bold text-gray-900">
                                ₹{pkg.subscriberPrice}
                              </span>
                              <span className="text-lg text-gray-500 line-through">
                                ₹{pkg.price}
                              </span>
                              <Badge className="bg-green-100 text-green-700">
                                Save ₹{pkg.price - pkg.subscriberPrice}
                              </Badge>
                            </div>
                          </div>
                        </div>
                        
                        <div className="space-y-2 mb-6">
                          {pkg.features.map((feature, index) => (
                            <div key={index} className="flex items-start space-x-2">
                              <CheckCircle className="w-4 h-4 text-green-600 mt-1 flex-shrink-0" />
                              <span className="text-gray-700 text-sm">{feature}</span>
                            </div>
                          ))}
                        </div>
                        
                        <Button 
                          onClick={() => handleConsultationBooking(pkg)}
                          className={`w-full ${
                            pkg.popular 
                              ? 'bg-orange-600 hover:bg-orange-700' 
                              : 'bg-green-600 hover:bg-green-700'
                          }`}
                        >
                          <Phone className="w-4 h-4 mr-2" />
                          Book via WhatsApp
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Consultation Form */}
            <Card className="border-orange-200">
              <CardHeader className="bg-orange-50">
                <CardTitle className="flex items-center space-x-2 text-orange-700">
                  <User className="w-5 h-5" />
                  <span>Quick Consultation Form</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <Input
                  placeholder="Full Name"
                  value={consultationForm.name}
                  onChange={(e) => setConsultationForm({...consultationForm, name: e.target.value})}
                />
                <Input
                  type="tel"
                  placeholder="Phone Number"
                  value={consultationForm.phone}
                  onChange={(e) => setConsultationForm({...consultationForm, phone: e.target.value})}
                />
                <Input
                  type="number"
                  placeholder="Age"
                  value={consultationForm.age}
                  onChange={(e) => setConsultationForm({...consultationForm, age: e.target.value})}
                />
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    placeholder="Current Weight (kg)"
                    value={consultationForm.currentWeight}
                    onChange={(e) => setConsultationForm({...consultationForm, currentWeight: e.target.value})}
                  />
                  <Input
                    placeholder="Target Weight (kg)"
                    value={consultationForm.targetWeight}
                    onChange={(e) => setConsultationForm({...consultationForm, targetWeight: e.target.value})}
                  />
                </div>
                <Textarea
                  placeholder="Dietary restrictions, allergies, or preferences"
                  value={consultationForm.dietaryRestrictions}
                  onChange={(e) => setConsultationForm({...consultationForm, dietaryRestrictions: e.target.value})}
                  rows={3}
                />
                <Textarea
                  placeholder="Medical history or current medications"
                  value={consultationForm.medicalHistory}
                  onChange={(e) => setConsultationForm({...consultationForm, medicalHistory: e.target.value})}
                  rows={3}
                />
              </CardContent>
            </Card>

            {/* Quick Stats */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <TrendingUp className="w-5 h-5 text-blue-600" />
                  <span>Why Choose GEETH365 Nutrition?</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                    <span className="text-sm font-medium text-blue-900">Certified Nutritionists</span>
                    <Badge className="bg-blue-100 text-blue-700">RD Qualified</Badge>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                    <span className="text-sm font-medium text-green-900">Success Rate</span>
                    <Badge className="bg-green-100 text-green-700">85%+</Badge>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                    <span className="text-sm font-medium text-purple-900">Follow-up Support</span>
                    <Badge className="bg-purple-100 text-purple-700">WhatsApp</Badge>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
                    <span className="text-sm font-medium text-orange-900">Pan-India Service</span>
                    <Badge className="bg-orange-100 text-orange-700">Available</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Emergency Contact */}
            <Card className="bg-red-50 border-red-200">
              <CardContent className="p-6 text-center">
                <AlertCircle className="w-8 h-8 text-red-600 mx-auto mb-3" />
                <h3 className="font-semibold text-red-900 mb-2">Medical Emergency?</h3>
                <p className="text-red-700 text-sm mb-4">
                  For immediate medical attention, call emergency services first.
                </p>
                <div className="space-y-2">
                  <a href="tel:108" className="block">
                    <Button className="w-full bg-red-600 hover:bg-red-700 text-sm">
                      Emergency: 108
                    </Button>
                  </a>
                  <a href="https://wa.me/917337541113?text=Need urgent nutrition guidance" target="_blank" rel="noopener noreferrer">
                    <Button variant="outline" className="w-full border-red-300 text-red-600 text-sm">
                      Nutrition Emergency
                    </Button>
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Nutrition;